import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import BookConsultationButton from './BookConsultationButton';
import Container from './ui/Container';

const NAV_ITEMS = [
  { to: '/services', label: 'Services' },
  { to: '/projects', label: 'Work' },
  { to: '/blog', label: 'Blog' },
  { to: '/about', label: 'About' },
];

interface RouteLinkProps {
  to: string;
  end?: boolean;
  className?: string;
  children: React.ReactNode;
  suppressActive?: boolean;
}

// Plain Link when suppressed: NavLink always sets aria-current="page" on a match.
const RouteLink: React.FC<RouteLinkProps> = ({ suppressActive = false, end, ...props }) =>
  suppressActive ? <Link {...props} /> : <NavLink end={end} {...props} />;

const Header = styled.header`
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 1000;
`;

const Bar = styled.div`
  height: var(--nav-h);
  background: rgba(7, 12, 22, 0.88);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border);
`;

const Inner = styled(Container)`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
`;

const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  min-height: 44px;
  border-radius: 6px;
  flex-shrink: 0;

  img:first-child {
    height: 28px;
    width: auto;
  }

  img:last-child {
    height: 17px;
    width: auto;
  }
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;

  @media (max-width: 959px) {
    display: none;
  }
`;

const NavItem = styled(RouteLink)`
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 0.85rem;
  border-radius: var(--radius-sm);
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--text-2);
  transition: color 0.18s ease, background-color 0.18s ease;

  &:hover {
    color: var(--text-1);
    background: var(--surface);
  }

  &.active {
    color: var(--text-1);
  }

  &.active::after {
    content: '';
    position: absolute;
    left: 0.85rem;
    right: 0.85rem;
    bottom: 6px;
    height: 2px;
    border-radius: 2px;
    background: var(--accent);
  }
`;

const DesktopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 959px) {
    display: none;
  }
`;

const MobileActions = styled.div`
  display: none;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 959px) {
    display: flex;
  }
`;

const CompactBook = styled.div`
  @media (max-width: 519px) {
    display: none;
  }
`;

const MenuButton = styled.button`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-strong);

  span {
    display: block;
    width: 18px;
    height: 2px;
    border-radius: 2px;
    background: var(--text-1);
  }
`;

const Backdrop = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1001;
  background: rgba(4, 8, 15, 0.7);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`;

const Drawer = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1002;
  width: min(360px, 88vw);
  display: flex;
  flex-direction: column;
  padding: 1rem var(--gutter) 2rem;
  background: var(--bg-1);
  border-left: 1px solid var(--border);
  box-shadow: var(--shadow-2);
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition: transform 0.25s ease, visibility 0.25s;
  overflow-y: auto;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: calc(var(--nav-h) - 1rem);
  margin-bottom: 1.5rem;
`;

const DrawerTitle = styled.span`
  font-size: var(--fs-eyebrow);
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-3);
`;

const CloseButton = styled.button`
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-strong);

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 18px;
    height: 2px;
    border-radius: 2px;
    background: var(--text-1);
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const DrawerNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  a {
    display: flex;
    align-items: center;
    min-height: 52px;
    padding: 0 0.75rem;
    border-radius: var(--radius-sm);
    font-family: var(--font-display);
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-2);
  }

  a:hover,
  a.active {
    color: var(--text-1);
    background: var(--surface);
  }

  a.active {
    box-shadow: inset 3px 0 0 var(--accent);
  }
`;

const DrawerFooter = styled.div`
  margin-top: auto;
  padding-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ContactLink = styled(RouteLink)`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 0.85rem;
  border-radius: var(--radius-sm);
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--text-2);

  &:hover,
  &.active {
    color: var(--text-1);
  }
`;

const Logo: React.FC = () => (
  <LogoLink to="/" aria-label="CuttingEdge DevOps home">
    <img src="/images/LogoPlain.webp" alt="" width={128} height={79} />
    <img src="/images/logo.webp" alt="" width={640} height={96} />
  </LogoLink>
);

const Navbar: React.FC<{ suppressActive?: boolean }> = ({ suppressActive = false }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!open) {
      if (wasOpen.current) menuButtonRef.current?.focus({ preventScroll: true });
      wasOpen.current = false;
      return;
    }
    wasOpen.current = true;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const firstLink = drawerRef.current?.querySelector<HTMLElement>('nav a');
    firstLink?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onResize = () => {
      if (window.innerWidth >= 960) setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  return (
    <Header>
      <Bar>
        <Inner>
          <Logo />

          <DesktopNav aria-label="Main">
            {NAV_ITEMS.map((item) => (
              <NavItem key={item.to} to={item.to} suppressActive={suppressActive}>
                {item.label}
              </NavItem>
            ))}
          </DesktopNav>

          <DesktopActions>
            <ContactLink to="/contact" suppressActive={suppressActive}>
              Contact
            </ContactLink>
            <BookConsultationButton />
          </DesktopActions>

          <MobileActions>
            <CompactBook>
              <BookConsultationButton label="Book a call" />
            </CompactBook>
            <MenuButton
              ref={menuButtonRef}
              type="button"
              aria-label="Open menu"
              aria-expanded={open}
              aria-controls="mobile-menu"
              onClick={() => setOpen(true)}
            >
              <span />
              <span />
              <span />
            </MenuButton>
          </MobileActions>
        </Inner>
      </Bar>

      <Backdrop $open={open} onClick={close} aria-hidden="true" />
      <Drawer
        id="mobile-menu"
        ref={drawerRef}
        $open={open}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
      >
        <DrawerHeader>
          <DrawerTitle>Menu</DrawerTitle>
          <CloseButton type="button" aria-label="Close menu" onClick={close} />
        </DrawerHeader>
        <DrawerNav aria-label="Mobile">
          <RouteLink to="/" end suppressActive={suppressActive}>
            Home
          </RouteLink>
          {NAV_ITEMS.map((item) => (
            <RouteLink key={item.to} to={item.to} suppressActive={suppressActive}>
              {item.label}
            </RouteLink>
          ))}
          <RouteLink to="/contact" suppressActive={suppressActive}>
            Contact
          </RouteLink>
        </DrawerNav>
        <DrawerFooter>
          <BookConsultationButton size="lg" fullWidth onClick={close} />
        </DrawerFooter>
      </Drawer>
    </Header>
  );
};

export default Navbar;
